import { useState } from "react";
import type React from "react";

// ─── TYPES ───────────────────────────────────────────────────────────────────
type CardType = "lpo" | "partnership" | null;

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  firmName: string;
  role: string;
  monthlyMatters: string;
  leadType: string;
}

const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbxCOMqzpz98m8CQuTYGN8eXjJUzGkrf21Vvp4Gz1L227JPjNHHMHi8pTZhgKYnj9gQP/exec";

async function submitToGoogleSheets(data: FormData): Promise<boolean> {
  try {
    await fetch(GOOGLE_SCRIPT_URL, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return true;
  } catch {
    return false;
  }
}

// ─── ISO BADGE ────────────────────────────────────────────────────────────────
function IsoBadge() {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "4px 10px",
        border: "1px solid #d1d5db",
        borderRadius: "4px",
        fontSize: "11px",
        fontFamily: "SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif",
        color: "#6b7280",
        letterSpacing: "0.04em",
        background: "#f9fafb",
        whiteSpace: "nowrap" as const,
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
      ISO/IEC 27001:2022
    </span>
  );
}

// ─── CARD COMPONENT ───────────────────────────────────────────────────────────
function ServiceCard({
  type,
  selected,
  onSelect,
}: {
  type: "lpo" | "partnership";
  selected: boolean;
  onSelect: () => void;
}) {
  const isLpo = type === "lpo";
  const font = "SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif";

  return (
    <div
      onClick={onSelect}
      style={{
        border: selected ? "2px solid #1a1a1a" : "1px solid #e5e7eb",
        borderRadius: "12px",
        padding: "32px",
        cursor: "pointer",
        background: selected ? "#f9fafb" : "#fff",
        transition: "all 0.2s ease",
        boxShadow: selected
          ? "0 8px 30px rgba(0,0,0,0.08)"
          : "0 1px 4px rgba(0,0,0,0.04)",
        // flex column so CTA sticks to bottom
        display: "flex",
        flexDirection: "column" as const,
        height: "100%",
      }}
    >
      {/* Tag */}
      <div style={{ marginBottom: "18px" }}>
        <span
          style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: "3px",
            background: isLpo ? "#1a1a1a" : "#f3f4f6",
            color: isLpo ? "#fff" : "#374151",
            fontSize: "11px",
            fontFamily: font,
            letterSpacing: "0.08em",
            textTransform: "uppercase" as const,
          }}
        >
          {isLpo ? "Legal Process Outsourcing" : "Partnership Model"}
        </span>
      </div>

      {/* Title */}
      <h2
        style={{
          fontSize: "22px",
          fontFamily: font,
          fontWeight: 700,
          color: "#111827",
          lineHeight: 1.3,
          margin: "0 0 14px 0",
        }}
      >
        {isLpo
          ? "Build a Scalable Legal Support Team Without Hiring In-House"
          : "Pay Only When Cases Complete"}
      </h2>

      {/* Description — flexGrow pushes CTA to bottom */}
      <p
        style={{
          fontSize: "14px",
          color: "#6b7280",
          lineHeight: 1.75,
          margin: "0 0 28px 0",
          fontFamily: font,
          flexGrow: 1,
        }}
      >
        {isLpo
          ? "Our trained legal professionals act as an extension of your firm, providing operational and legal support across the conveyancing lifecycle so your solicitors can focus on client relationships."
          : "For firms looking to scale rapidly without operational risk. You only pay us a percentage of the legal fee when a case successfully completes — if a case doesn’t complete, there’s no payment to us. This model reduces fixed costs while helping you increase monthly completions"}
      </p>

      {/* CTA — always pinned to bottom */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onSelect();
        }}
        style={{
          width: "100%",
          padding: "15px 24px",
          background: "#1a1a1a",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          fontSize: "14px",
          fontWeight: 600,
          cursor: "pointer",
          letterSpacing: "0.02em",
          fontFamily: font,
          transition: "background 0.15s",
          WebkitTapHighlightColor: "transparent" as unknown as string,
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = "#333"; }}
        onMouseOut={(e) => { e.currentTarget.style.background = "#1a1a1a"; }}
      >
        {isLpo ? "Get My Outsourcing Plan →" : "Explore Partnership Model →"}
      </button>
    </div>
  );
}

// ─── FORM FIELD (defined outside FormPopup to prevent remount on every keystroke) ──
const FONT = "SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif";

const labelStyle: React.CSSProperties = {
  fontSize: "12px",
  fontWeight: 600,
  color: "#374151",
  letterSpacing: "0.03em",
  display: "block",
  marginBottom: "5px",
};

function Field({
  label,
  value,
  onChange,
  type = "text",
  required = true,
  placeholder = "",
  hasError = false,
  errorMsg = "",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  hasError?: boolean;
  errorMsg?: string;
}) {
  return (
    <div>
      <label style={labelStyle}>
        {label} {required && <span style={{ color: "#ef4444" }}>*</span>}
      </label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={hasError ? "field-input field-input--error" : "field-input"}
      />
      {hasError && errorMsg && (
        <span style={{ fontSize: "11px", color: "#ef4444", marginTop: "3px", display: "block" }}>
          {errorMsg}
        </span>
      )}
    </div>
  );
}

// ─── FORM POPUP ───────────────────────────────────────────────────────────────
function FormPopup({
  leadType,
  onClose,
  onSuccess,
}: {
  leadType: CardType;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const font = FONT;

  const [form, setForm] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    firmName: "",
    role: "",
    monthlyMatters: "",
    leadType: leadType === "lpo" ? "Legal Process Outsourcing" : "Partnership Model",
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validate = () => {
    const e: Partial<FormData> = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.lastName.trim()) e.lastName = "Required";
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email))
      e.email = "Valid email required";
    if (!form.firmName.trim()) e.firmName = "Required";
    if (!form.role) e.role = "Required";
    if (!form.monthlyMatters) e.monthlyMatters = "Required";
    return e;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    await submitToGoogleSheets(form);
    setLoading(false);
    onSuccess();
  };

  const set = (k: keyof FormData) => (v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: "" }));
  };

  return (
    <>
      {/* Drag handle (visible on mobile) */}
      <div
        style={{
          width: "36px",
          height: "4px",
          background: "#e5e7eb",
          borderRadius: "2px",
          margin: "12px auto 0",
        }}
        className="drag-handle"
      />

      {/* Modal Header */}
      <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f3f4f6" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ marginBottom: "8px" }}>
              <span
                style={{
                  display: "inline-block",
                  padding: "3px 10px",
                  background: "#f3f4f6",
                  borderRadius: "3px",
                  fontSize: "11px",
                  fontFamily: font,
                  color: "#6b7280",
                  letterSpacing: "0.06em",
                }}
              >
                {form.leadType}
              </span>
            </div>
            <h3 style={{ fontSize: "20px", fontFamily: font, fontWeight: 700, color: "#111827", margin: 0 }}>
              Tell Us About Your Firm
            </h3>
            <p style={{ fontSize: "13px", color: "#9ca3af", marginTop: "5px", marginBottom: 0, lineHeight: 1.5 }}>
              Our team will send you a tailored overview within 24 hours.
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "#f3f4f6",
              border: "none",
              cursor: "pointer",
              color: "#6b7280",
              fontSize: "16px",
              padding: "6px 10px",
              borderRadius: "6px",
              lineHeight: 1,
              flexShrink: 0,
              marginLeft: "12px",
              WebkitTapHighlightColor: "transparent" as unknown as string,
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Form Body */}
      <div style={{ padding: "20px 24px 36px", display: "flex", flexDirection: "column", gap: "14px" }}>
        {/* Name Row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
          <Field label="First Name" value={form.firstName} onChange={set("firstName")} hasError={!!errors.firstName} errorMsg={errors.firstName} />
          <Field label="Last Name" value={form.lastName} onChange={set("lastName")} hasError={!!errors.lastName} errorMsg={errors.lastName} />
        </div>

        <Field label="Email Address" value={form.email} onChange={set("email")} type="email" hasError={!!errors.email} errorMsg={errors.email} />
        <Field label="Phone Number" value={form.phone} onChange={set("phone")} type="tel" required={false} placeholder="Optional" />
        <Field label="Law Firm Name" value={form.firmName} onChange={set("firmName")} hasError={!!errors.firmName} errorMsg={errors.firmName} />

        {/* Role */}
        <div>
          <label style={labelStyle}>
            Role in Firm <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <select
            value={form.role}
            onChange={(e) => {
              setForm((p) => ({ ...p, role: e.target.value }));
              setErrors((p) => ({ ...p, role: "" }));
            }}
            className={errors.role ? "field-input field-input--error" : "field-input"}
            style={{ color: form.role ? "#111827" : "#9ca3af", cursor: "pointer" }}
          >
            <option value="" disabled>Select your role</option>
            {["Director / Partner", "Solicitor", "Conveyancer", "Practice Manager", "Operations Manager", "Other"].map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          {errors.role && (
            <span style={{ fontSize: "11px", color: "#ef4444", marginTop: "3px", display: "block" }}>
              {errors.role}
            </span>
          )}
        </div>

        {/* Monthly Matters */}
        <div>
          <label style={labelStyle}>
            How many conveyancing matters per month?{" "}
            <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
            {["0 – 20", "20 – 50", "50 – 100", "100+"].map((opt) => (
              <button
                key={opt}
                onClick={() => {
                  setForm((p) => ({ ...p, monthlyMatters: opt }));
                  setErrors((p) => ({ ...p, monthlyMatters: "" }));
                }}
                style={{
                  padding: "12px 10px",
                  border: form.monthlyMatters === opt ? "2px solid #1a1a1a" : "1px solid #d1d5db",
                  borderRadius: "6px",
                  background: form.monthlyMatters === opt ? "#1a1a1a" : "#fff",
                  color: form.monthlyMatters === opt ? "#fff" : "#374151",
                  fontSize: "14px",
                  fontWeight: 500,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  fontFamily: font,
                  WebkitTapHighlightColor: "transparent" as unknown as string,
                }}
              >
                {opt}
              </button>
            ))}
          </div>
          {errors.monthlyMatters && (
            <span style={{ fontSize: "11px", color: "#ef4444", marginTop: "3px", display: "block" }}>
              {errors.monthlyMatters}
            </span>
          )}
        </div>

        {/* ISO */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px", paddingTop: "2px" }}>
          <IsoBadge />
          <span style={{ fontSize: "11px", color: "#9ca3af" }}>Your data is handled securely</span>
        </div>

        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading}
          style={{
            padding: "16px",
            background: loading ? "#9ca3af" : "#1a1a1a",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            fontSize: "15px",
            fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            letterSpacing: "0.02em",
            fontFamily: font,
            transition: "background 0.15s",
            marginTop: "4px",
            WebkitTapHighlightColor: "transparent" as unknown as string,
          }}
        >
          {loading ? "Submitting…" : "Send My Details →"}
        </button>

        <p style={{ fontSize: "11px", color: "#9ca3af", textAlign: "center", margin: 0 }}>
          We'll respond within 24 hours. No spam, ever.
        </p>
      </div>
    </>
  );
}

// ─── THANK YOU STATE ──────────────────────────────────────────────────────────
function ThankYou({ onReset }: { onReset: () => void }) {
  const font = "SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif";
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#fafafa", padding: "40px 20px" }}>
      <div style={{ textAlign: "center", maxWidth: "480px", width: "100%" }}>
        <div
          style={{
            width: "64px", height: "64px", borderRadius: "50%",
            background: "#f0fdf4", border: "1.5px solid #bbf7d0",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 28px",
          }}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h2 style={{ fontSize: "clamp(22px, 5vw, 30px)", fontFamily: font, fontWeight: 700, color: "#111827", lineHeight: 1.3, marginBottom: "16px" }}>
          Thank You — One of Our Team Will Contact You Shortly
        </h2>
        <p style={{ fontSize: "15px", color: "#6b7280", lineHeight: 1.7, marginBottom: "36px" }}>
          Our team will review your information and contact you within{" "}
          <strong style={{ color: "#111827" }}>24 hours</strong> to discuss how we can support your firm.
        </p>
        <a
          href="https://outlook.office.com/book/ConnectwithTellida@tellida.org/?ismsaljsauthenabled"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "block", padding: "16px 28px", background: "#1a1a1a", color: "#fff",
            borderRadius: "8px", fontSize: "15px", fontWeight: 600,
            textDecoration: "none", letterSpacing: "0.02em", marginBottom: "16px",
          }}
        >
          Book a 15-Minute Introduction Call →
        </a>
        <button
          onClick={onReset}
          style={{ background: "none", border: "none", color: "#9ca3af", fontSize: "13px", cursor: "pointer", textDecoration: "underline", fontFamily: font }}
        >
          Back to options
        </button>
      </div>
    </div>
  );
}

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
export default function TempleadPage() {
  const font = "SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif";
  const [activeCard, setActiveCard] = useState<CardType>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleCardSelect = (type: "lpo" | "partnership") => {
    setActiveCard(type);
    setShowForm(true);
  };

  if (submitted)
    return <ThankYou onReset={() => { setSubmitted(false); setActiveCard(null); }} />;

  return (
    <>
      <style>{`
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; -webkit-font-smoothing: antialiased; }

        @keyframes slideUp {
          from { transform: translateY(40px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        .card-row {
          display: flex;
          gap: 20px;
          align-items: stretch;
        }
        .card-row > .card-wrap {
          flex: 1;
          min-width: 0;
          display: flex;
        }
        .card-row > .card-wrap > div {
          width: 100%;
        }

        /* Mobile: stack cards */
        @media (max-width: 639px) {
          .card-row {
            flex-direction: column;
          }
        }

        /* Modal: bottom sheet on mobile, centered dialog on desktop */
        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.45);
          display: flex;
          align-items: flex-end;
          justify-content: center;
          z-index: 1000;
          backdrop-filter: blur(4px);
          animation: fadeIn 0.2s ease;
        }
        .modal-sheet {
          background: #fff;
          border-radius: 16px 16px 0 0;
          width: 100%;
          max-height: 92vh;
          overflow-y: auto;
          box-shadow: 0 -8px 40px rgba(0,0,0,0.15);
          animation: slideUp 0.28s cubic-bezier(0.32, 0.72, 0, 1);
        }
        @media (min-width: 640px) {
          .modal-overlay {
            align-items: center;
          }
          .modal-sheet {
            border-radius: 12px;
            max-width: 540px;
            margin: 20px;
            max-height: 90vh;
          }
          .drag-handle { display: none; }
        }

        /* Trust footer wraps on small screens */
        .trust-footer {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 20px;
          flex-wrap: wrap;
          row-gap: 10px;
        }

        button, a { -webkit-tap-highlight-color: transparent; }

        .field-input {
          display: block;
          width: 100%;
          padding: 11px 14px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 15px;
          font-family: SF Pro Display, -apple-system, BlinkMacSystemFont, sans-serif;
          background: #fff;
          outline: none;
          box-sizing: border-box;
          -webkit-appearance: none;
          transition: border-color 0.15s;
        }
        .field-input:focus {
          border: 1.5px solid #1a1a1a;
        }
        .field-input--error {
          border: 1.5px solid #ef4444;
        }
        .field-input--error:focus {
          border: 1.5px solid #ef4444;
        }
      `}</style>

      <div style={{ minHeight: "100vh", background: "#fafafa", fontFamily: font }}>

        {/* Hero */}
        <div style={{ maxWidth: "680px", margin: "0 auto", padding: "56px 20px 36px", textAlign: "center" }}>
          <h1
            style={{
              fontSize: "clamp(22px, 5vw, 40px)",
              fontFamily: font,
              fontWeight: 700,
              color: "#111827",
              lineHeight: 1.2,
              marginBottom: "14px",
              letterSpacing: "-0.02em",
            }}
          >
            Two Smart Ways UK Law Firms Are Scaling Their Conveyancing Operations
          </h1>
          <p style={{ fontSize: "clamp(13px, 2vw, 15px)", color: "#6b7280", lineHeight: 1.7, margin: "0 0 6px" }}>
            Choose the engagement model that works best for your firm.
          </p>
          <p style={{ fontSize: "13px", color: "#9ca3af", lineHeight: 1.5, margin: 0 }}>
            All services delivered under strict{" "}
            <strong style={{ color: "#6b7280" }}>ISO/IEC 27001:2022</strong>{" "}
            information security standards.
          </p>
        </div>

        {/* Step Indicator */}
        <div style={{ display: "flex", justifyContent: "center", gap: "6px", marginBottom: "28px", padding: "0 20px", flexWrap: "wrap" }}>
          {[
            { n: "01", label: "Choose your model" },
            { n: "02", label: "Tell us about your firm" },
            { n: "03", label: "We'll be in touch" },
          ].map((step, i) => (
            <div key={step.n} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                <span style={{ fontFamily: font, fontSize: "11px", color: i === 0 ? "#111827" : "#d1d5db", fontWeight: 600 }}>
                  {step.n}
                </span>
                <span style={{ fontSize: "12px", color: i === 0 ? "#374151" : "#d1d5db" }}>
                  {step.label}
                </span>
              </div>
              {i < 2 && <span style={{ color: "#e5e7eb", fontSize: "12px", margin: "0 2px" }}>›</span>}
            </div>
          ))}
        </div>

        {/* Cards */}
        <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "0 20px 72px" }}>
          <div className="card-row">
            <div className="card-wrap">
              <ServiceCard type="lpo" selected={activeCard === "lpo"} onSelect={() => handleCardSelect("lpo")} />
            </div>
            <div className="card-wrap">
              <ServiceCard type="partnership" selected={activeCard === "partnership"} onSelect={() => handleCardSelect("partnership")} />
            </div>
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && activeCard && (
        <div
          className="modal-overlay"
          onClick={(e) => { if (e.target === e.currentTarget) setShowForm(false); }}
        >
          <div className="modal-sheet">
            <div className="drag-handle" style={{ width: "36px", height: "4px", background: "#e5e7eb", borderRadius: "2px", margin: "12px auto 0" }} />
            <FormPopup
              leadType={activeCard}
              onClose={() => setShowForm(false)}
              onSuccess={() => { setShowForm(false); setSubmitted(true); }}
            />
          </div>
        </div>
      )}
    </>
  );
}